// src/hooks/useAntiPiracy.ts
// 🔒 SISTEMA ANTIPIRATARIA COMPLETO
// ✅ PERMITE seleção (para anotações)
// ✅ PERMITE Ctrl+V (colar — não expõe conteúdo)
// ❌ BLOQUEIA cópia, print, download de imagens
// 📱 PROTEÇÃO MOBILE: sobreposição preta ao tirar screenshot

import { useEffect } from 'react';

export const useAntiPiracy = (enabled: boolean = true) => {
    useEffect(() => {
        if (!enabled) {
            console.log('🔓 [ANTIPIRACY] Proteção desabilitada (modo admin)');
            return;
        }

        console.log('🔒 [ANTIPIRACY] Ativando proteção completa...');

        // ========== 1. BLOQUEIO DE CÓPIA (MAS NÃO SELEÇÃO E NÃO COLAGEM) ==========
        const preventCopy = (e: ClipboardEvent) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('🚫 Cópia bloqueada');
            return false;
        };

        // ========== 2. BLOQUEIO DE TECLAS DE ATALHO ==========
        const preventKeys = (e: KeyboardEvent) => {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

            if (ctrlKey) {
                const key = e.key.toLowerCase();

                // ✅ Ctrl+V LIBERADO — colar não expõe conteúdo da plataforma
                if (key === 'v') return;

                // ❌ Ctrl+C, Ctrl+X, Ctrl+P, Ctrl+S bloqueados
                if (['c', 'x', 'p', 's'].includes(key)) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log(`🚫 Atalho bloqueado: ${e.key}`);
                    return false;
                }
            }

            // PrintScreen
            if (e.key === 'PrintScreen') {
                e.preventDefault();
                console.log('🚫 PrintScreen bloqueado');

                document.body.classList.add('print-blocked');
                setTimeout(() => {
                    document.body.classList.remove('print-blocked');
                }, 300);
                return false;
            }

            // F12 (DevTools)
            if (e.key === 'F12') {
                e.preventDefault();
                console.log('🚫 F12 bloqueado');
                return false;
            }
        };

        // ========== 3. BLOQUEIO DE MENU DE CONTEXTO ==========
        const preventContextMenu = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('🚫 Menu de contexto bloqueado');
            return false;
        };

        // ========== 4. BLOQUEIO DE DRAG & DROP DE IMAGENS ==========
        const preventDrag = (e: DragEvent) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'IMG') {
                e.preventDefault();
                e.stopPropagation();
                console.log('🚫 Drag de imagem bloqueado');
                return false;
            }
        };

        // ========== 5. PROTEÇÃO CONTRA PRINT (beforeprint) ==========
        const beforePrintHandler = () => {
            console.log('🚫 Tentativa de impressão detectada');
            document.body.classList.add('printing-blocked');
        };

        const afterPrintHandler = () => {
            document.body.classList.remove('printing-blocked');
        };

        // ========== 6. PROTEÇÃO MOBILE: SCREENSHOT VIA VISIBILITYCHANGE ==========
        // Quando o usuário tira screenshot no mobile, o app vai brevemente para
        // segundo plano (visibilityState = 'hidden'). Mostramos sobreposição preta
        // nesse momento, corrompendo visualmente a captura.
        let screenshotOverlay: HTMLDivElement | null = null;
        let overlayTimeout: ReturnType<typeof setTimeout> | null = null;

        const createScreenshotOverlay = () => {
            if (screenshotOverlay) return;
            const overlay = document.createElement('div');
            overlay.id = 'antipiracy-screenshot-overlay';
            overlay.style.cssText = `
                position: fixed;
                inset: 0;
                z-index: 2147483647;
                background: #000;
                pointer-events: none;
                opacity: 0;
                transition: opacity 0.05s;
            `;
            document.body.appendChild(overlay);
            screenshotOverlay = overlay;
        };

        const showOverlay = () => {
            if (!screenshotOverlay) createScreenshotOverlay();
            // Força um frame de repaint com a tela preta antes de restaurar
            requestAnimationFrame(() => {
                if (screenshotOverlay) screenshotOverlay.style.opacity = '1';
            });
        };

        const hideOverlay = () => {
            if (overlayTimeout) clearTimeout(overlayTimeout);
            // Mantém visível por 400ms para garantir que o screenshot seja corrompido
            overlayTimeout = setTimeout(() => {
                if (screenshotOverlay) screenshotOverlay.style.opacity = '0';
            }, 400);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                showOverlay();
            } else {
                hideOverlay();
            }
        };

        // ========== 7. INJETAR CSS DE PROTEÇÃO ==========
        const styleTag = document.createElement('style');
        styleTag.id = 'antipiracy-protection-styles';
        styleTag.innerHTML = `
            /* ✅ PERMITIR SELEÇÃO (para anotações/grifos) */
            /* NÃO bloquear user-select */

            /* 🔒 Proteção de imagens */
            body.antipiracy-active img {
                -webkit-user-select: none !important;
                -moz-user-select: none !important;
                -ms-user-select: none !important;
                user-select: none !important;
                -webkit-user-drag: none !important;
                -khtml-user-drag: none !important;
                -moz-user-drag: none !important;
                -o-user-drag: none !important;
                pointer-events: none !important;
            }

            /* 🔒 Permitir cliques em imagens APENAS para zoom */
            body.antipiracy-active .card-content img,
            body.antipiracy-active .support-content-body img,
            body.antipiracy-active .rrp-content img {
                pointer-events: auto !important;
                cursor: zoom-in !important;
            }

            /* 🔒 Ocultar durante print screen (desktop) */
            body.print-blocked,
            body.print-blocked * {
                opacity: 0 !important;
                visibility: hidden !important;
                background: black !important;
            }

            /* 🔒 Proteção durante impressão (desktop) */
            body.printing-blocked,
            body.printing-blocked * {
                visibility: hidden !important;
                background: black !important;
                color: black !important;
            }

            /* 🔒 Media query para impressão */
            @media print {
                body.antipiracy-active,
                body.antipiracy-active * {
                    background: black !important;
                    color: black !important;
                    visibility: hidden !important;
                }
            }

            /* 📱 Sobreposição de screenshot mobile */
            #antipiracy-screenshot-overlay {
                position: fixed;
                inset: 0;
                z-index: 2147483647;
                background: #000;
                pointer-events: none;
            }
        `;

        document.head.appendChild(styleTag);
        document.body.classList.add('antipiracy-active');

        // ========== 8. CRIAR OVERLAY E APLICAR TODOS OS EVENT LISTENERS ==========
        createScreenshotOverlay();

        document.addEventListener('copy', preventCopy, true);
        document.addEventListener('cut', preventCopy, true);
        // ✅ 'paste' NÃO bloqueado — Ctrl+V deve funcionar normalmente
        document.addEventListener('keydown', preventKeys, true);
        document.addEventListener('contextmenu', preventContextMenu, true);
        document.addEventListener('dragstart', preventDrag, true);
        window.addEventListener('beforeprint', beforePrintHandler);
        window.addEventListener('afterprint', afterPrintHandler);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        console.log('✅ [ANTIPIRACY] Proteção completa ativada (Ctrl+V e seleção permitidos)');

        // ========== 9. CLEANUP ==========
        return () => {
            console.log('🔓 [ANTIPIRACY] Removendo proteção...');

            document.removeEventListener('copy', preventCopy, true);
            document.removeEventListener('cut', preventCopy, true);
            document.removeEventListener('keydown', preventKeys, true);
            document.removeEventListener('contextmenu', preventContextMenu, true);
            document.removeEventListener('dragstart', preventDrag, true);
            window.removeEventListener('beforeprint', beforePrintHandler);
            window.removeEventListener('afterprint', afterPrintHandler);
            document.removeEventListener('visibilitychange', handleVisibilityChange);

            if (overlayTimeout) clearTimeout(overlayTimeout);
            if (screenshotOverlay) screenshotOverlay.remove();

            const existingStyle = document.getElementById('antipiracy-protection-styles');
            if (existingStyle) existingStyle.remove();

            document.body.classList.remove('antipiracy-active');
            document.body.classList.remove('print-blocked');
            document.body.classList.remove('printing-blocked');
        };
    }, [enabled]);
};